import * as React from "preact";
import { FunctionComponent } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";

import Cookies from "js-cookie";

import utils from "../../helpers/utils";
import Results from "./components/Results";
import SearchBox from "./components/SearchBox";
import { IFilterCategory } from "./components/Filters";

export interface ISearchParams {
  query?: string;
  pageSize?: string | number;
  page?: string | number;
  superCategory?: string;
}

interface IAppProps {
  apiUrl?: string;
  filtersApiUrl?: string;
  searchBoxOnly: boolean;
  inStandalonePageBlock?: boolean;
  settings?: any;
  id: string;
  recentSearchApiUrl?: string;
}

const App: FunctionComponent<IAppProps> = ({
  apiUrl,
  filtersApiUrl,
  searchBoxOnly,
  settings,
  id,
  inStandalonePageBlock,
  recentSearchApiUrl,
}) => {
  const [firstRun, setFirstRun] = useState(true);
  const [resetFilters, setResetFilters] = useState<boolean>(true); // Change value to trigger a reset
  const [data, setData] = useState<any>(null); // All, unfiltered data used for all tabs. This doesn't change throughout user journey and is used to show available tabs etc.
  const [allFilters, setAllFilters] = useState<IFilterCategory[] | null>(null);
  const [filters, setFilters] = useState<IFilterCategory[] | null>(null);
  const [preSelectedFilters, setPreSelectedFilters] = useState<string[]>([]);
  const [filteredAvailableFilters, setFilteredAvailableFilters] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingFilters, setLoadingFilters] = useState<boolean>(true);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [dataSaveStates, setDataSaveStates] = useState<{contentId: string, state: boolean}[]>([]);
  const [fetchDataSaveLoading, setFetchDataSaveLoading] = useState<boolean>(true);
  const urlParams = utils.decodeParams();
  const query = urlParams.query || urlParams["input-autocomplete"]; // input-autocomplete is forced when using predictive search. We don't control prop name.
  const [currentPage, setCurrenPage] = useState<number>(parseInt(urlParams.page, 10) || 1); // Page size to be used to paginate on the FE. This is used in the app.
  const params = {...urlParams};
  const { standaloneSuperCategory, placeholder, pageSize, recentSearchEnabled, getSaveListApiUrl, onlySavedItemsEnabled } = settings;
  const superCategory = standaloneSuperCategory || params.superCategory;
  const [restrictToSavedItems, setRestrictToSavedItems] = useState<boolean>(onlySavedItemsEnabled);

  const scrollRef = useRef<HTMLDivElement>(null);

  let lockedFilters = [];

  if (settings.lockedFilters) {
    lockedFilters = settings.lockedFilters.split(",");
  }

  const handleUpdateRecentSearches = (data: string[]) => {
    setRecentSearches(data);
  }

  useEffect(() => {

    let queryFilters = [];
      if (urlParams.filters) {
          queryFilters = urlParams.filters.split(",");
      }

    if (apiUrl && !searchBoxOnly) {
      fetchResults(queryFilters, lockedFilters).then(() => {
        // Preselect any filters from the URL for the ability to link to a pre-filtered search page
        if (urlParams.filters) {
          const newFilters = urlParams.filters.split(",");
          setPreSelectedFilters(newFilters);
        }
        setCurrenPage(currentPage);
        setFirstRun(false);
        executeScroll();
      });
    }
  }, [apiUrl, searchBoxOnly, firstRun, superCategory]);

  useEffect(() => {
    // This is an unfiltered search returning all results (firstRun = true).
    // Needed in order to get data for all tabs & filters.
    if (data && (!allFilters || loadingFilters) && filtersApiUrl) {
      const fetchFilters = async () => {
        try {
          const res = await fetch(filtersApiUrl);
          if (res.status !== 200) {
            console.error(`There was a problem. Status Code: ${res.status}`);
            return;
          }

          const filters = await res.json();
          setAllFilters(filters);
          if (filters) {
            const facetFilters = data.facets[1].facets.map((item) => item.displayName).flat();
            const facetFilterList = [...new Set(facetFilters)];
            const parsedFilters = JSON.parse(JSON.stringify(filters));

            setFilters(processAvailableFilters(parsedFilters, facetFilterList));
            setLoadingFilters(false);
          }
          processAvailableFilteredFilters();

        } catch(error) {
          console.log(error);
          setAllFilters(null);
          setLoadingFilters(false);
        }
      }

      fetchFilters()
    }
  }, [data, allFilters]);

  const processAvailableFilteredFilters = () => {
    const filteredFilters = data.facets[3].facets.map(x => x.displayName);
    setFilteredAvailableFilters(filteredFilters);
  }

  const fetchDataSaveStates = async () => {
    if (!getSaveListApiUrl) {
      setFetchDataSaveLoading(false);
      return;
    }

    try {
          const response = await fetch(getSaveListApiUrl, {
          method: "POST",
          body: JSON.stringify(data.items.map(item => item.contentId)),
          headers: {
            "Content-Type": "application/json",
            "Accept": "application:json"
          }
      });

      if (response.status !== 200) {
        console.error(`There was a problem. Status Code: ${response.status}`);
        return;
      }

      const saveData = await response.json();

      setDataSaveStates(saveData);
    } catch(error) {
      console.error(`There was a problem. Error: ${error}`);
    } finally {
      setFetchDataSaveLoading(false);
    }
  }

  useEffect(() => {
    if (!data || data?.items?.length === 0) {
      return;
    }

    fetchDataSaveStates();
  }, [data]);

  const getFiltersForFacets = () => {
    const facetFilters = data.facets[0].facets.map((item) => item.displayName).flat();
    const facetFilterList = [...new Set(facetFilters)];
    return facetFilterList;
  };

  useEffect(() => {
    if (!loading) {
      executeScroll();
    }
  }, [loading]);

  useEffect(() => {
    setCurrenPage(currentPage);
  }, [currentPage]);

  const doResetFilters = (resetPreselections: boolean) => {
    setResetFilters(true);
    if (resetPreselections) {
      setPreSelectedFilters([]);
    }
    if (!data) {
      return;
    }
    const { items } = data;
    // On first run we get all results (for all super categories).
    // Upon filtering/changing tabs - the original list is untouched and only refresh the super category.
    if (items && items.length && allFilters) {
      //Filter out all filters without matching results for a current superCategory

      const newItemsFilters = getFiltersForFacets();
      const newFilters = JSON.parse(JSON.stringify(allFilters));
      setFilters(processAvailableFilters(newFilters, newItemsFilters));
    }
    setResetFilters(!resetFilters);
  };

  const processAvailableGrandChildrenFilters = (newFilters: any[], children: any[]) => {
    return children?.filter?.((grandChild) => {
      return newFilters.includes(grandChild.id);
    })
  }

  const processAvailableChildFilters = (newFilters: any[], children: any[]) => {
    return children?.filter?.((child) => {
      child.children = processAvailableGrandChildrenFilters(newFilters, child.children);
      const foundChild = newFilters.includes(child.id);
      return (child.selectable && foundChild) || (child.children && child.children.length);
    });
  }

  const processAvailableFilters = (allFilters: any, newFilters: any): IFilterCategory[] => {
    return allFilters?.filter?.((af) => {
      const selectable = af.selectable;
      const parent = newFilters.includes(af.id);
      af.children = processAvailableChildFilters(newFilters, af.children);
      // Filter by superCategeory filters
      return (selectable && !!parent) || (af.children && af.children.length);
    });
  };

  const handleChangeSuperCategory = (): void => {
    doResetFilters(true);
    delete params.filters;
    delete params.queryTyped;
    resetPageNumber();
    window.history.pushState(params, "Parameterised Search", "?" + utils.serializeParams(params));
    executeScroll();
    processAvailableFilteredFilters();
  };

  const handleChangePage = (page: number): void => {
    setCurrenPage(page);

    let currentfilters = [];
    if (params.filters) {
      currentfilters = params.filters.split(",");
    }

    const newParams = {...params, page};
    window.history.pushState(params, "Parameterised Search", "?" + utils.serializeParams(newParams));
    fetchResults(currentfilters, settings.lockedFilters, page);
    processAvailableFilteredFilters();
    executeScroll();
  };

  const handleSort = (e): void => {
    const sortOption = e.target.value;
    params.sortBy = sortOption;

    let currentfilters = [];
    if (params.filters) {
      currentfilters = params.filters.split(",");
    }
    resetPageNumber();
    delete params.queryTyped;
    window.history.pushState(params, "Parameterised Search", "?" + utils.serializeParams(params));
    processAvailableFilteredFilters();
    fetchResults(currentfilters, settings.lockedFilters, 1);
  };

  const handleApplyFilters = (newFilters: string[], isClearFilter: boolean): void => {
    if (newFilters) {
      params.filters = newFilters;
    } else {
      delete params.filters;
      setRestrictToSavedItems(false);
    }

    if (isClearFilter) {
      setRestrictToSavedItems(false);
    }

    resetPageNumber();
    delete params.queryTyped;
    window.history.pushState(params, "Parameterised Search", "?" + utils.serializeParams(params));
    fetchResults(newFilters, settings.lockedFilters, 1, !isClearFilter ? restrictToSavedItems : false);
  };

  const getQueryForApi = (
    newFilters?: string[],
    lockedFilters?: string[],
    isAll?: boolean,
    pageNumber?: number,
    restrictToSavedItems?: boolean
  ): string => {
    // This could be written more efficiently, i.e. with destructuring / creating a new object instead of accessing params so many times
    if (isAll || firstRun) {
      delete params.superCategory; // Get all results upfront to show the superCategories (tabs) correctly - ignore superCategory
    }
    if (standaloneSuperCategory) {
      params.superCategory = standaloneSuperCategory;
    }
    delete params.Region; // Incorrect capitalization or role and region
    delete params.Role;
    params.pageSize = pageSize; // Request 1000 results upfront (limit in search API). Pagination happens on FE.
    params.role = Cookies.get("Role") || undefined;
    params.region = Cookies.get("Region") || undefined;
    params.selectedFilters = newFilters || filters; // i.e. ["75154", "75161"];
    params.lockedFilters = lockedFilters || undefined;
    params.sortBy = utils.getSortOrder(params, settings);

    params.query = params.query || params["input-autocomplete"];
    params.page = currentPage;
    if (pageNumber) {
      params.page = pageNumber;
    }

    if (restrictToSavedItems) {
      params.restrictToSavedItems = restrictToSavedItems;
    }

    delete params["input-autocomplete"];
    const queryString = utils.serializeParams(params);
    return queryString;
  };

  const executeScroll = () => {
    if (urlParams.queryTyped || urlParams.page || urlParams.superCategeory) {
      const el = scrollRef?.current?.getBoundingClientRect();
      const header = document.getElementsByClassName("js-header")[0];
      let scrollHeight = (el ? el.top : 0) + window.pageYOffset;
      if (header && !utils.isMobile()) {
        const headerHeight = header.getBoundingClientRect().height;
        scrollHeight -= headerHeight;
      }
      window.scrollTo({top: scrollHeight, behavior: "smooth"});
    }
  };

  const fetchResults = (
    newFilters?: string[],
    lockedFilters?: string[],
    pageNumber?: number,
    savedItemsToggleState?: boolean
  ): Promise<void> => {
    if (pageNumber) {
      setCurrenPage(pageNumber);
    }
    setLoadingFilters(true);
    const queryString = getQueryForApi(
      newFilters || preSelectedFilters,
      lockedFilters || undefined,
      !superCategory,
      pageNumber,
      onlySavedItemsEnabled || (typeof(savedItemsToggleState) === 'boolean' ? savedItemsToggleState : restrictToSavedItems),
    );
    setLoading(true);
    return fetch(`${apiUrl}?${queryString}`)
      .then(function (res) {
        if (res.status !== 200) {
          console.error(`There was a problem. Status Code: ${res.status}`);
          setLoading(false);
          return;
        }
        res
          .json()
          .then(function (data) {
            // On firstRun fetch unfiltered / all results - for tabs
            setData(data);

            // Preselect any passed in filters to update mobile/desktop lists
            // or the filters from the URL for the ability to link to a pre-filtered search page
            if (superCategory && (newFilters || urlParams.filters)) {
              const filtersToSet = newFilters || urlParams.filters.split(",");
              setPreSelectedFilters(filtersToSet);
              const filteredFilters = data.facets[3].facets.map(x => x.displayName);
              setFilteredAvailableFilters(filteredFilters);
            }

            setLoading(false);
          })
          .catch((err) => {
            console.error(err);
            setData(null);
            setLoading(false);
          });
      })
      .catch((err) => {
        console.log(err);
        setData(null);
        setLoading(false);
      });
  };

  const resetPageNumber = () => {
    delete params.page; // Reset page (as there might not be enough results anymore)
    setCurrenPage(1);
  };

  const setNewQuery = (params) => {
    resetPageNumber();
    if (searchBoxOnly) {
      const searchPageUrl = settings.searchPageUrl;
      window.location.href = `${searchPageUrl}?${params}`; // Open search page
    } else {
      doResetFilters(true);
      setResetFilters(true);
      window.history.pushState(params, "Parameterised Search", "?" + params);
      setFirstRun(true);
      setRestrictToSavedItems(false);
    }
  };

  const toggleRestrictToSavedItems = (state: boolean) => {
    setRestrictToSavedItems(state);
    let currentfilters = [];
    if (params.filters) {
      currentfilters = params.filters.split(",");
    }
    resetPageNumber();
    delete params.queryTyped;
    window.history.pushState(params, "Parameterised Search", "?" + utils.serializeParams(params));
    fetchResults(currentfilters, settings.lockedFilters, 1, state);
  }

  window.onpopstate = () => {
    setFirstRun(true); // Upon pressing the back button, act as if it's a new pageload and reconsider all the URL parameters & fetch all data just in case.
  };

  if (searchBoxOnly) {
    return (
      <SearchBox
        query={query}
        params={params}
        setNewQuery={setNewQuery}
        inNav={true}
        inStandalonePageBlock={inStandalonePageBlock}
        placeholder={placeholder}
        id={id}
        recentSearchApiUrl={recentSearchApiUrl}
        handleUpdateRecentSearches={handleUpdateRecentSearches}
        recentSearches={recentSearches}
        recentSearchEnabled={recentSearchEnabled}
      />
    );
  }

  return (
    <div>
      <div className="search-header">
        <div className="container">
          <div className="container__inner">
            <SearchBox
              query={query}
              params={params}
              setNewQuery={setNewQuery}
              placeholder={placeholder}
              id={id}
              recentSearchApiUrl={recentSearchApiUrl}
              handleUpdateRecentSearches={handleUpdateRecentSearches}
              recentSearches={recentSearches}
              recentSearchEnabled={recentSearchEnabled}
            />
          </div>
        </div>
      </div>

      <div
        className="container search-results__container"
        ref={scrollRef}
      >
        <div className="container__liner">
          <Results
            data={data}
            params={params}
            settings={settings}
            loading={loading}
            filters={filters}
            loadingFilters={loadingFilters}
            availableFilteredFilters={filteredAvailableFilters}
            resetFilters={resetFilters}
            preSelectedFilters={preSelectedFilters}
            currentPage={currentPage}
            handleChangeSuperCategory={handleChangeSuperCategory}
            handleChangePage={handleChangePage}
            handleFilters={handleApplyFilters}
            handleSort={handleSort}
            sortField={utils.getSortOrder(params, settings)}
            dataSaveStates={dataSaveStates}
            fetchDataSaveLoading={fetchDataSaveLoading}
            restrictToSavedItems={restrictToSavedItems}
            toggleRestrictToSavedItems={toggleRestrictToSavedItems}
          />
        </div>
      </div>
    </div>
  );
};

export default App;
